import { useCallback } from "react";

import { FormatCurrencyOptions, formatCmOutcome, formatNumericCurrency, useGetCarbonProgramByFarmId, useUserContext } from "utils";

import { CURRENCY_CODE, CurrencyCode } from "consts";

type FormatCurrencyFn = (value: number, opts?: Omit<FormatCurrencyOptions, "currency" | "currencyDisplay">) => string;

type SoilSightCurrency = {
  currencyCode: CurrencyCode;
  currencySymbol: string;
  isZloty: boolean;
  formatCurrency: FormatCurrencyFn;
  formatCmOutcome: FormatCurrencyFn;
};

/**
 * Returns the currency code and symbol for SoilSight, taken from the farm's carbon program — the same
 * rule the API applies when converting the values it serves. Farms without a program use EUR (€).
 */
export const useSoilSightCurrency = (): SoilSightCurrency => {
  const { user } = useUserContext();
  const farmId = user?.farm?.id;
  const { data: carbonProgram } = useGetCarbonProgramByFarmId(farmId ?? undefined);

  const currencyCode = carbonProgram?.currency ?? CURRENCY_CODE.EUR;
  const useZloty = currencyCode === CURRENCY_CODE.PLN;

  const formatCurrency = useCallback<FormatCurrencyFn>(
    (value, opts = {}) => formatNumericCurrency(value, { ...opts, currency: currencyCode, currencyDisplay: "narrowSymbol" }),
    [currencyCode]
  );

  const formatCmOutcomeDisplay = useCallback<FormatCurrencyFn>(
    (value, opts = {}) => formatCmOutcome(value, { ...opts, currency: currencyCode, currencyDisplay: "narrowSymbol" }),
    [currencyCode]
  );

  return {
    currencyCode,
    currencySymbol: useZloty ? "zł" : "€",
    isZloty: useZloty,
    formatCurrency,
    formatCmOutcome: formatCmOutcomeDisplay,
  };
};
